import { useMemo, useState, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getPatientNotes,
  getPatientCareplanNotes,
} from "../../../services/patient/details";
import styles from "../../../styles/patient-page-modules/Notes.module.css";
import expandIcon from "../../../assets/icons/notes/expand.svg";
import NotesControls from "./controls";
import NotesList from "./list";
import NotesDrawer from "./NotesDrawer";
import Tooltip from "../../shared-modules/Tooltip";

/**
 * Notes — timeline-style notes panel inside the right card's second
 * .cardSection. No CollapsableSection wrapper; the panel renders its
 * own header (title + sort) and a scrollable list of timeline rows.
 *
 * Per Figma 357:1427, the page-level RPM/CCM tab (chatBotType) drives
 * which notes look "active" (navy dot) vs "inactive" (gray dot). The
 * panel always shows all note types (RPM, CCM, Careplan).
 */
const Notes = ({ chatBotType, onNoteClick }) => {
  const locationId = JSON.parse(sessionStorage.getItem("current_loc"))?.id;
  const patientId = sessionStorage.getItem("_pt_id");
  const [currentSort, setCurrentSort] = useState("latest");
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const expandTriggerRef = useRef(null);

  const {
    data: notes,
    isLoading,
    error,
    refetch,
  } = useQuery(
    ["patient-notes", patientId, currentSort],
    () => getPatientNotes(null, currentSort, patientId, locationId),
    {
      enabled: !!patientId,
      staleTime: 2 * 60 * 1000,
    }
  );

  const { data: careplanNotes } = useQuery(
    ["patient-careplan-notes", patientId],
    () => getPatientCareplanNotes(patientId, locationId),
    {
      enabled: !!patientId,
      select: (data) =>
        data.map((note) => ({
          ...note,
          note_type: "careplan_note",
          note_text: note.note,
          // Careplan notes carry no created_at; updated_at is the
          // finalize timestamp. Aliasing it keeps the row date and the
          // merge-sort consistent with RPM/CCM notes.
          created_at: note.created_at ?? note.updated_at,
          assigned_by_name: note.finalized_by_name,
        })),
    }
  );

  // Merge both note streams + apply client-side sort by created_at to
  // keep the timeline ordering stable when one query refetches.
  const items = useMemo(() => {
    const merged = [...(notes || []), ...(careplanNotes || [])];
    merged.sort((a, b) => {
      const ta = new Date(a.created_at || 0).getTime();
      const tb = new Date(b.created_at || 0).getTime();
      return currentSort === "oldest" ? ta - tb : tb - ta;
    });
    return merged;
  }, [notes, careplanNotes, currentSort]);

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <h3 className={styles.title}>Notes</h3>
        <div className={styles.headerActions}>
          <Tooltip content="View all notes" position="left">
            <button
              type="button"
              ref={expandTriggerRef}
              className={styles.expandBtn}
              onClick={() => setIsDrawerOpen(true)}
              aria-label="View all notes"
            >
              <img src={expandIcon} alt="" aria-hidden="true" />
            </button>
          </Tooltip>
          <NotesControls
            defaultSort={currentSort}
            handleSort={(value) => setCurrentSort(value)}
          />
        </div>
      </div>

      <NotesList
        notes={items}
        isLoading={isLoading}
        error={error}
        refetch={refetch}
        onNoteClick={onNoteClick}
        chatBotType={chatBotType}
      />

      <NotesDrawer
        isOpen={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
          expandTriggerRef.current?.focus();
        }}
        chatBotType={chatBotType}
        onNoteClick={onNoteClick}
      />
    </div>
  );
};

export default Notes;
